import React, { useState, useEffect, useCallback, useRef } from "react";
import { intl, Notifications } from "dmsbasereact";
import { useNavigate } from "react-router-dom";
import { APP_ROUTES } from "../../utils/NamedRoutes";
import { asyncExportApi } from "./api";
import { EXPORT_JOB_STATUS, POLL_INTERVAL_MS } from "./constants";

/**
 * Hook that fetches the current user's export jobs and polls at a fixed
 * interval while the page is mounted. Fires a toast whenever any job
 * transitions to READY or ERROR — works even if the user was already on
 * this page when the job was created.
 */
export const useExportJobs = () => {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const prevStatusRef = useRef({});
    const initializedRef = useRef(false);
    const navigate = useNavigate();

    const notifyTransitions = useCallback((updatedJobs) => {
        if (!initializedRef.current) return;

        updatedJobs.forEach((job) => {
            const prev = prevStatusRef.current[job.pk];
            if (prev === undefined || prev === job.job_status) return;

            if (job.job_status === EXPORT_JOB_STATUS.READY) {
                Notifications.toast({
                    title: (
                        <span>
                            {intl.get("ExportReady.Toast").d("Your export is ready for download")}
                            {" — "}
                            <span
                                onClick={() => navigate(APP_ROUTES.MyDownloads)}
                                style={{ textDecoration: "underline", cursor: "pointer", fontWeight: 600 }}
                            >
                                {intl.get("MyDownloads").d("My Downloads")}
                            </span>
                        </span>
                    ),
                    type: Notifications.TYPE_SUCCESS,
                    timeout: 10000,
                });
            } else if (job.job_status === EXPORT_JOB_STATUS.ERROR) {
                Notifications.toast({
                    title: intl.get("ExportFailed.Toast").d("Export failed. Please try again."),
                    type: Notifications.TYPE_ERROR,
                    timeout: 10000,
                });
            }
        });

        prevStatusRef.current = Object.fromEntries(updatedJobs.map((j) => [j.pk, j.job_status]));
    }, [navigate]);

    useEffect(() => {
        let cancelled = false;

        const doFetch = async (isInitial) => {
            try {
                const data = await asyncExportApi.listMyJobs();
                if (cancelled) return;

                if (isInitial) {
                    // Seed the status map so subsequent polls can detect transitions
                    prevStatusRef.current = Object.fromEntries(data.map((j) => [j.pk, j.job_status]));
                    initializedRef.current = true;
                } else {
                    notifyTransitions(data);
                }

                setJobs(data);
            } catch {
                // keep previous state on network error
            } finally {
                if (isInitial && !cancelled) setLoading(false);
            }
        };

        doFetch(true);
        // Always poll while mounted — catches new jobs created after initial load
        const interval = setInterval(() => doFetch(false), POLL_INTERVAL_MS);

        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, [notifyTransitions]);

    const dismiss = useCallback(async (pk) => {
        try {
            await asyncExportApi.dismiss(pk);
        } catch {
            // Server returns {"ok": true} but keep going regardless
        }
        setJobs((prev) => prev.filter((j) => j.pk !== pk));
        Notifications.toast({
            title: intl.get("ExportJob.Dismissed").d("File deleted successfully"),
            type: Notifications.TYPE_SUCCESS,
            timeout: 3000,
        });
        delete prevStatusRef.current[pk];
    }, []);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const data = await asyncExportApi.listMyJobs();
            notifyTransitions(data);
            setJobs(data);
            return data;
        } catch {
            return [];
        } finally {
            setLoading(false);
        }
    }, [notifyTransitions]);

    return { jobs, loading, dismiss, refresh };
};
